/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { If, Then } from "react-if";
import { Button, FlexContainer, Text, theme } from "@nordcloud/gnui";
import type { AppliedFilter } from "~/hooks/useAppliedFilters";
import { isEmpty } from "~/tools";
import { MultiValuePopover } from "./MultiValuePopover";
import { RemoveFilterIconButton } from "./RemoveFilterIconButton";

export type { AppliedFilter } from "~/hooks/useAppliedFilters";

type Props<TKey extends string> = {
  filters: AppliedFilter<TKey>[];
  onRemoveFilter: (key: TKey) => void;
  onRemoveFilterValue: (key: TKey, value: string) => void;
  onClearAllFilters: () => void;
};

export function AppliedFiltersBar<TKey extends string>({
  filters,
  onRemoveFilter,
  onRemoveFilterValue,
  onClearAllFilters,
}: Props<TKey>) {
  const [hoveredFilterKey, setHoveredFilterKey] = useState<TKey | null>(null);
  const [hoveredValueKey, setHoveredValueKey] = useState<string | null>(null);
  const isMultiValueFilterHovered = (filter: AppliedFilter<TKey>) =>
    hoveredFilterKey === filter.key &&
    !!filter.values &&
    filter.values.length > 1;

  return (
    <If condition={!isEmpty(filters)}>
      <Then>
        <div
          style={{
            marginTop: theme.spacing.spacing02,
            paddingTop: theme.spacing.spacing02,
            borderTop: `1px solid ${theme.color.border.border01}`,
            backgroundColor: theme.color.background.ui02,
          }}
        >
          <FlexContainer
            alignItems="flex-end"
            wrap="wrap"
            style={{
              columnGap: theme.spacing.spacing03,
              rowGap: theme.spacing.spacing01,
            }}
          >
            {filters.map((filter) => (
              <div
                key={filter.key}
                data-testid={`applied-filter-${filter.key}`}
                style={{
                  position: "relative",
                  marginBottom: 0,
                }}
                onMouseEnter={() =>
                  setHoveredFilterKey(
                    filter.values && filter.values.length > 1
                      ? filter.key
                      : null
                  )
                }
                onMouseLeave={() => setHoveredFilterKey(null)}
              >
                <Text
                  tag="div"
                  mb={theme.spacing.spacing01}
                  style={{
                    fontSize: theme.fontSizes.sm,
                    fontWeight: theme.fontWeights.medium,
                    color: theme.color.text.text02,
                  }}
                >
                  {filter.label}:
                </Text>
                <Button
                  severity="low"
                  size="md"
                  style={{
                    minHeight: theme.spacing.spacing07,
                    padding: "0 0",
                    pointerEvents: "none",
                    textTransform: "none",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: theme.spacing.spacing01,
                      lineHeight: 1,
                      textTransform: "none",
                    }}
                  >
                    <span style={{ lineHeight: 1, textTransform: "none" }}>
                      {filter.value}
                    </span>
                    <RemoveFilterIconButton
                      ariaLabel={`remove-filter-${filter.key}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemoveFilter(filter.key);
                      }}
                    />
                  </span>
                </Button>
                <If condition={isMultiValueFilterHovered(filter)}>
                  <Then>
                    <MultiValuePopover
                      filterKey={filter.key}
                      values={filter.values ?? []}
                      valueLabels={filter.valueLabels}
                      hoveredValueKey={hoveredValueKey}
                      onValueMouseEnter={setHoveredValueKey}
                      onValueMouseLeave={() => setHoveredValueKey(null)}
                      onRemoveFilterValue={onRemoveFilterValue}
                    />
                  </Then>
                </If>
              </div>
            ))}
            <div
              style={{
                flex: "0 0 auto",
                width: "fit-content",
                marginBottom: 0,
              }}
            >
              <Button
                severity="low"
                size="md"
                icon="filterClear"
                onClick={onClearAllFilters}
              >
                Clear Filters
              </Button>
            </div>
          </FlexContainer>
        </div>
      </Then>
    </If>
  );
}
